/**
 * PlanEntitiesFixturePage — a dev-only harness for the Floor Plan design's *Plan entities*
 * (`floor-plan-page-plan-entities`, spec R8).
 *
 * ## Why a fixture page
 *
 * The Floor Plan step draws the `marks` look (since `floor-plan-page-stage`), but only the states its
 * twin happens to have — error, to check and identify from the Checks model; lit and arrived have no
 * producer until the page features land. This page puts every space look and every machine mark on one invented
 * plan, with controls for the transient ones, so a real browser can drive and measure them (jsdom
 * lays nothing out) and the before/after captures can be taken on a fixed rig.
 *
 * Nothing here is real data and nothing is written anywhere: the stores are seeded synchronously and
 * no save is wired. As with `/plan-camera-fixture`, the route exists only outside production and the
 * fixture constants live inside the component, so the bundle carries none of them.
 *
 * `?stress=1` swaps in 300 machines and 40 spaces, for the frame-time measurement of the dots.
 *
 * `?host=1` mounts the planner as the onboarding page's machine card host (`itemEditing="host"`,
 * inside a `ToastProvider` as that page has it), so resting on a machine shows its hover card — the
 * frame `plan-view-model`'s capture pair needs.
 *
 * The plan driver's parity rig (`onboarding-3d-plan-plan-driver-core`, `planSceneDriver.pw.spec.ts`):
 *
 * - `?placed=1` places the First Floor at (4 m, −3 m), turned 30°, so Edit floor folds a turn into
 *   the view and Build floors draws the floor inside a placed group;
 * - `?readonly=1` swaps the planner for two read-only plans over the same layout, an interactive one
 *   (`fixture-active`) and a passive 320 × 240 corner picture (`fixture-passive`), each registered
 *   with `window.__planScene` under that name.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import {
  PLAN_ENTITY_LEGEND,
  PlanLegend,
  ReadOnlyCanvas,
  TwinPlanner,
  plannerTheme,
} from '@repo/ui/planner';
import type { PlanEntityMarks, MachineState, SpaceState } from '@repo/ui/planner';
import { Tag, ToastProvider } from '@repo/design-system';
import { useEditorStore, useFloorplanStore } from '@repo/front-end-state';
import type {
  FloorPlannerItem,
  FloorPlannerLayout,
  FloorPlannerSurface,
  FloorPlannerWall,
} from '@repo/frontend-types';
import { semantic, space, typeRole } from '@repo/design-tokens';
import { PlanRendererProviders } from '../../components/3D/planView/PlanRendererProviders';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: ${semantic.surface.app};
`;
const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[2]};
  padding: ${space[3]} ${space[4]};
`;
const Label = styled.span`
  color: ${semantic.text.secondary};
  font-size: ${typeRole.caption1.fontSize};
  margin-left: ${space[3]};
`;
const PlannerRegion = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
`;
const ReadOnlyCorner = styled.div`
  position: absolute;
  right: ${space[4]};
  bottom: ${space[4]};
  width: 320px;
  height: 240px;
  z-index: 2;
`;
const LegendCorner = styled.div`
  position: absolute;
  left: ${space[4]};
  bottom: ${space[4]};
  z-index: 2;
`;

const TWIN = 'FIX-ENT';

const PlanEntitiesFixturePage = () => {
  const stress = useMemo(() => new URLSearchParams(window.location.search).has('stress'), []);
  const host = useMemo(() => new URLSearchParams(window.location.search).has('host'), []);
  const placed = useMemo(() => new URLSearchParams(window.location.search).has('placed'), []);
  const readOnly = useMemo(() => new URLSearchParams(window.location.search).has('readonly'), []);
  const [readOnlySelected, setReadOnlySelected] = useState<string | null>(null);
  const seededLayout = useFloorplanStore(s => s.layout);
  const [arriving, setArriving] = useState(true);
  /** Bumped by *Jump*: each press hands the planner a new one-id `jumped` set, as a pick does. */
  const [jumps, setJumps] = useState(0);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    const wall = (ax: number, ay: number, bx: number, by: number): FloorPlannerWall => ({
      a: { x: ax, y: ay },
      b: { x: bx, y: by },
      thickness: 0.2,
      openings: [],
    });
    const box = (x: number, y: number, w: number, h: number) => [
      { x, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
    ];
    const space = (
      refid: string,
      name: string,
      code: string | undefined,
      poly: ReturnType<typeof box>,
    ) => ({ refid, name, entityCode: code, poly }) as FloorPlannerSurface;
    const machine = (refid: string, name: string, x: number, y: number, rotation = 0) =>
      ({ refid, name, x, y, z: 0, rotation }) as FloorPlannerItem;

    let surfaces: FloorPlannerSurface[];
    let items: FloorPlannerItem[];
    if (stress) {
      surfaces = Array.from({ length: 40 }, (_u, i) =>
        space(
          `S-${i}`,
          `Space ${i + 1}`,
          `${TWIN}-Z${String(i + 1).padStart(3, '0')}`,
          box((i % 8) * 6, Math.floor(i / 8) * 6, 5, 5),
        ),
      );
      items = Array.from({ length: 300 }, (_u, i) =>
        machine(
          `M-${i}`,
          `Machine ${i + 1}`,
          0.8 + (i % 30) * 1.6,
          0.8 + Math.floor(i / 30) * 2.9,
          (i * 30) % 360,
        ),
      );
    } else {
      surfaces = [
        space('Z-FUNC', 'Functional', `${TWIN}-Z004`, box(1, 1, 9, 6)),
        space('S-STUDIO', 'Studio 1', `${TWIN}-S006`, box(12, 1, 6, 6)),
        space('Z-CARDIO', 'Cardio', `${TWIN}-Z002`, box(20, 1, 9, 6)),
        space('A-NEW', 'Spin Bay', undefined, box(1, 9, 6, 5)),
        space('Z-STRENGTH', 'Strength', `${TWIN}-Z001`, box(9, 9, 8, 5)),
        space('Z-STRETCH', 'Stretch', `${TWIN}-Z003`, box(19, 9, 5, 5)),
        // An adopted space whose BID ends like Functional's: the tag reads the code as given (Z004 twice).
        space('S-BOX', 'Boxing', 'FIX-OLD-Z004', box(25, 9, 4, 5)),
      ];
      const named: FloorPlannerItem[] = [
        machine('M-ERR', 'Integrity 95T-12', 3, 3),
        machine('M-CHK', 'Leg Press', 6, 3, 90),
        machine('M-ID1', 'Treadmill 1', 22, 3),
        machine('M-ID2', 'Treadmill 2', 24, 3),
        machine('M-LIT', 'Treadmill 3', 26, 3),
        machine('M-ARR', 'Rower 1', 14, 3),
        machine('M-SEL', 'Treadmill 72', 16, 5, 180),
        machine('M-HOV', 'Bike 4', 27.5, 5.5, 45),
      ];
      const ok = Array.from({ length: 22 }, (_u, i) =>
        machine(
          `M-OK-${i}`,
          `Machine ${i + 1}`,
          10 + (i % 11) * 1.6,
          10.5 + Math.floor(i / 11) * 2,
          (i * 45) % 360,
        ),
      );
      items = [...named, ...ok];
    }

    const layout: FloorPlannerLayout = {
      name: 'Plan entities fixture',
      floors: [
        {
          id: 1,
          name: 'Ground Floor',
          level: 0,
          height: 3,
          designs: [
            {
              name: 'Current',
              areas: [],
              walls: [
                wall(0, 0, 30, 0),
                wall(30, 0, 30, 15),
                wall(30, 15, 0, 15),
                wall(0, 15, 0, 0),
              ],
              surfaces,
              items,
              cameras: [],
            },
          ],
        },
        {
          id: 2,
          name: 'First Floor',
          level: 1,
          height: 3,
          ...(placed ? { placement: { x: 4, y: -3, rotation: 30 } } : {}),
          designs: [
            {
              name: 'Current',
              areas: [],
              walls: [
                wall(2, 2, 18, 2),
                wall(18, 2, 18, 12),
                wall(18, 12, 2, 12),
                wall(2, 12, 2, 2),
              ],
              surfaces: [space('Z-UP', 'Mezzanine Gym', `${TWIN}-Z010`, box(3, 3, 14, 8))],
              items: [machine('M-UP-1', 'Bike 1', 6, 6), machine('M-UP-2', 'Bike 2', 9, 6)],
              cameras: [],
            },
          ],
        },
      ],
    };
    useFloorplanStore.getState().loadLayout(layout, 'FIXTURE', 'FIX', 'Plan entities fixture');
    useEditorStore.getState().clearHistory();
    useEditorStore.getState().clearModified();
    useEditorStore.setState({ selectedEntityIds: stress ? [] : ['M-SEL'] });
    setSeeded(true);
    return () => {
      useFloorplanStore.getState().reset();
    };
  }, [stress, placed]);

  /** Every state the plan can draw, as the check model will hand them over. */
  const entityMarks = useMemo<PlanEntityMarks>(() => {
    if (stress) {
      return {
        machines: new Map<string, MachineState>(
          Array.from({ length: 225 }, (_u, i): [string, MachineState] => [
            `M-${i}`,
            (['error', 'check', 'identify'] as const)[i % 3],
          ]),
        ),
      };
    }
    return {
      machines: new Map<string, MachineState>([
        ['M-ERR', 'error'],
        ['M-CHK', 'check'],
        ['M-ID1', 'identify'],
        ['M-ID2', 'identify'],
        ['M-LIT', 'identify'],
      ]),
      spaces: new Map<string, SpaceState>([
        ['Z-FUNC', 'error'],
        ['S-STUDIO', 'check'],
        ['S-BOX', 'error'],
      ]),
      // A host that sends only `lit` has it drawn as a search match.
      lit: new Set(['M-LIT', 'Z-CARDIO']),
      // The machine the identify card is open on: being edited.
      identifying: new Set(['M-ID2']),
      // The draft's own changes: one machine placed, one moved.
      changes: new Map<string, 'added' | 'moved'>([
        ['M-HOV', 'added'],
        ['M-CHK', 'moved'],
      ]),
      // The planner glows what ENTERS these sets, so *Replay arrival* empties them for a frame.
      arrived: arriving ? new Set(['M-ARR']) : new Set<string>(),
      drawn: arriving ? new Set(['A-NEW']) : new Set<string>(),
      ...(jumps > 0 ? { jumped: new Set(['M-ID1']) } : {}),
    };
  }, [stress, arriving, jumps]);

  return (
    <PlanRendererProviders twinRecord={null} twinBid={undefined}>
      {/* Renderer-switch R3.2: this fixture draws in 3D, as onboarding does. No twin record: the
          anchor falls back as onboarding's does with none. */}
      <Page>
        <Controls data-testid="fixture-controls">
          <Label>Arrival</Label>
          <Tag
            surface="on-solid"
            toggle
            active={false}
            onToggle={() => {
              setArriving(false);
              requestAnimationFrame(() => setArriving(true));
            }}
          >
            Replay arrival
          </Tag>
          <Tag surface="on-solid" toggle active={false} onToggle={() => setJumps(n => n + 1)}>
            Jump to Treadmill 1
          </Tag>
          <Label>Draw</Label>
          <Tag
            surface="on-solid"
            toggle
            active={false}
            onToggle={() =>
              useEditorStore.setState({ selectedEntityIds: [], toolMode: 'draw_space' })
            }
          >
            Start drawing
          </Tag>
          <Label>Select</Label>
          <Tag
            surface="on-solid"
            toggle
            active={false}
            onToggle={() => useEditorStore.setState({ selectedEntityIds: ['Z-STRENGTH'] })}
          >
            Strength (space)
          </Tag>
          <Tag
            surface="on-solid"
            toggle
            active={false}
            onToggle={() => useEditorStore.setState({ selectedEntityIds: ['M-SEL'] })}
          >
            Treadmill 72 (machine)
          </Tag>
        </Controls>
        <PlannerRegion data-testid="fixture-planner">
          {seeded && readOnly && seededLayout && (
            <ThemeProvider theme={plannerTheme}>
              <ReadOnlyCanvas
                layout={seededLayout}
                driverName="fixture-active"
                selectedId={readOnlySelected}
                onSelectEntity={setReadOnlySelected}
              />
              <ReadOnlyCorner>
                <ReadOnlyCanvas
                  layout={seededLayout}
                  driverName="fixture-passive"
                  interactive={false}
                />
              </ReadOnlyCorner>
            </ThemeProvider>
          )}
          {seeded && !readOnly && (
            <ThemeProvider theme={plannerTheme}>
              <HostToasts host={host}>
                <TwinPlanner
                  toolset="full"
                  showToolbar
                  showPlanModeSwitch
                  showFullscreenButton={false}
                  showPropertiesPanel={false}
                  entityMarks={entityMarks}
                  itemEditing={host ? 'host' : 'panel'}
                />
              </HostToasts>
            </ThemeProvider>
          )}
          {!readOnly && (
            <LegendCorner>
              <PlanLegend framed entries={PLAN_ENTITY_LEGEND} />
            </LegendCorner>
          )}
        </PlannerRegion>
      </Page>
    </PlanRendererProviders>
  );
};

/** As the host page has it: the machine card's host raises toasts, so it needs their provider. */
const HostToasts = ({ host, children }: { host: boolean; children: ReactNode }) =>
  host ? <ToastProvider>{children}</ToastProvider> : <>{children}</>;

export default PlanEntitiesFixturePage;
